import { useMemo, useState } from 'react'
import { images } from '../../../services/images.service'
import { HtmlContentRenderer } from './HtmlContentRenderer'
import { GreyDivider } from '../../ui/elements'
import { motion, AnimatePresence } from 'framer-motion'

function extractHeadingsFromContent(content) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(content, 'text/html')

  const headingElements = Array.from(doc.querySelectorAll('h2, h3, h4'))

  const headings = headingElements.map((heading, idx) => {
    const id = `heading-${idx}`
    heading.id = id
    return { id, text: heading.innerText }
  })

  return { updatedContent: doc.body.innerHTML, headings }
}

export function PostDetailsContent({ content }) {
  const [isTOCOpen, setIsTOCOpen] = useState(false)

  const { updatedContent, headings } = useMemo(
    () => extractHeadingsFromContent(content),
    [content]
  )

  return (
    <section
      className="post-content-wrapper mt-4"
      aria-labelledby="post-content-heading"
      aria-describedby="post-content-description"
      role="region"
    >
      <meta
        id="post-content-description"
        content="קטע זה כולל תוכן עניינים ופרטי הכתבה עצמה."
      />

      <nav
        className="flex justify-between items-center p-4 bg-accent cursor-pointer"
        aria-label="תפריט תוכן עניינים של הפוסט"
        role="navigation"
        aria-expanded={isTOCOpen}
        onClick={() => setIsTOCOpen((prev) => !prev)}
      >
        <h5 id="post-content-heading" className="text-softText text-h5">
          תוכן עניינים
        </h5>
        <img
          src={images.closeMenu}
          alt={isTOCOpen ? 'סגור תוכן עניינים' : 'פתח תוכן עניינים'}
          title={isTOCOpen ? 'סגור תוכן עניינים' : 'פתח תוכן עניינים'}
          className={`transition-transform duration-300 ${
            isTOCOpen ? 'rotate-180' : 'rotate-0'
          }`}
          role="button"
          aria-pressed={isTOCOpen}
        />
      </nav>

      <AnimatePresence initial={false}>
        {isTOCOpen && (
          <motion.ul
            key="toc"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="overflow-hidden mb-10 bg-accent"
            role="list"
            aria-label="רשימת כותרות מתוך התוכן"
          >
            <GreyDivider />
            <div className="p-8">
              {headings.map(({ id, text }) => (
                <li key={id} className="my-8 first:my-0">
                  <a
                    href={`#${id}`}
                    title={`מעבר ל-${text}`}
                    className="text-p text-white hover:text-primary transition-colors duration-200"
                  >
                    {text}
                  </a>
                </li>
              ))}
            </div>
          </motion.ul>
        )}
      </AnimatePresence>

      <article
        className="post-content mt-6 prose prose-invert max-w-none break-words whitespace-normal"
        role="article"
        aria-label="תוכן הפוסט המלא"
      >
        <HtmlContentRenderer html={updatedContent} />
      </article>
    </section>
  )
}
